import { deriveBrandingVariants } from '../utils/branding'

interface BrandPreviewHeaderProps {
  primaryColor: string
  accentColor?: string
  logoUrl?: string
  title: string
  subtitle?: string
  versionPillText?: string
  showDraftPill?: boolean
}

/**
 * Solid-color mockup of the real v3 dashboard header (template.ts, injectDashboardData) —
 * used anywhere a practitioner picks brand colors, so the preview they see matches what
 * actually ships instead of a gradient swatch. Keep in sync with the live header's chrome
 * and pill styling if either ever changes.
 */
export default function BrandPreviewHeader({ primaryColor, accentColor, logoUrl, title, subtitle, versionPillText, showDraftPill }: BrandPreviewHeaderProps) {
  const { primaryColorDark } = deriveBrandingVariants(primaryColor)
  const hasBadge = !!(versionPillText || showDraftPill)

  return (
    <div className="rounded-xl p-4 bg-white border border-gray-100 shadow-inner" style={{ borderTop: `4px solid ${primaryColor}` }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {logoUrl && (
            <>
              <img src={logoUrl} alt="Logo preview" className="w-11 h-11 object-contain rounded bg-white p-1 flex-shrink-0 border border-gray-100" />
              <div className="w-px self-stretch bg-gray-200 flex-shrink-0" />
            </>
          )}
          <div className="min-w-0">
            <div className="text-xs font-bold uppercase tracking-wider truncate" style={{ color: primaryColor }}>
              Workforce &amp; Workflow Reinvention
            </div>
            <div className="text-gray-900 font-bold text-lg truncate">{title}</div>
            {subtitle && <div className="text-gray-500 text-xs truncate">{subtitle}</div>}
          </div>
        </div>
        {hasBadge && (
          <div className="flex flex-col items-end gap-1 shrink-0">
            {versionPillText && (
              <span
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                style={{ background: `${primaryColor}1F`, color: primaryColorDark }}
              >
                {versionPillText}
              </span>
            )}
            {showDraftPill && (
              <span
                className="bg-white text-[9px] font-bold px-1.5 py-0.5 rounded-full border-[1.25px] whitespace-nowrap"
                style={{ borderColor: accentColor || primaryColor, color: accentColor || primaryColor }}
              >
                DRAFT FOR DISCUSSION
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
